import { Routes, Route } from "react-router-dom";
import { MAIN_ROUTES_URLS, USER_ROLES } from "../../constants/constants";
import { routeList } from "../../routes/routesList";
import { usePlatformOrg } from "../../utils/context/PlatformOrgContext";
import PlatformDashboard from "./PlatformDashboard";
import PlatformOrganizations from "./organizations";
import SupportRequests from "./support/SupportRequests";
import PlatformPaymentAccountsPage from "./payment-accounts";
import MyProfilePlatform from "./MyProfilePlatform";
import PlatformSettings from "./platform-settings/PlatformSettings";

// The organization pages the platform portal reuses when impersonating the selected org. Deliberately excludes:
// the org dashboard (id 1 — the platform keeps its own PlatformDashboard), Messaging (the platform role has no
// Message/Communication access on the backend), and the org-user-only settings shells (My Organization,
// Organization Accounts, org Platform Settings, PDF/email generators).
const REUSABLE_ORG_PATHS = new Set<string>([
  MAIN_ROUTES_URLS.UNITS,
  MAIN_ROUTES_URLS.PROPERTIES,
  MAIN_ROUTES_URLS.TENANTS,
  MAIN_ROUTES_URLS.SERVICE_REQUESTS,
  MAIN_ROUTES_URLS.CONTRACTORS,
  MAIN_ROUTES_URLS.PAYMENT,
  MAIN_ROUTES_URLS.TRANSACTION,
]);

/**
 * Route table for the platform super-admin portal. Home is the platform dashboard; the reused organization pages
 * are scoped to the org selected in the header switcher (via sessionStorage — see PlatformOrgContext). The whole
 * subtree is keyed by the selected org id so the org pages remount and re-read the id on a switch.
 */
const PlatformPortalRoutes = () => {
  const { selectedOrgId } = usePlatformOrg();
  const orgRoutes = (routeList[USER_ROLES[1]] || []).filter((r: any) =>
    REUSABLE_ORG_PATHS.has(r.path)
  );

  return (
    <Routes key={selectedOrgId ?? "none"}>
      {/* Dashboard stays platform-specific. */}
      <Route path={MAIN_ROUTES_URLS.HOME} element={<PlatformDashboard />} />

      {/* Reused organization pages, scoped to the selected org. */}
      {orgRoutes.map((r: any) => (
        <Route key={r.path} path={r.path} element={r.element} />
      ))}

      {/* Platform-only surfaces grouped under Platform Settings (reached from the top-right menu). */}
      <Route path={MAIN_ROUTES_URLS.PLATFORM_SETTINGS} element={<PlatformSettings />} />

      {/* Backward-compatible standalone routes (WF1 links + PlatformDashboard deep-links). */}
      <Route path={MAIN_ROUTES_URLS.PLATFORM_ORGANIZATIONS} element={<PlatformOrganizations />} />
      <Route path={MAIN_ROUTES_URLS.PLATFORM_SUPPORT} element={<SupportRequests />} />
      <Route path={MAIN_ROUTES_URLS.PLATFORM_PAYMENT_ACCOUNTS} element={<PlatformPaymentAccountsPage />} />
      <Route path={MAIN_ROUTES_URLS.MY_SETTINGS} element={<MyProfilePlatform />} />
    </Routes>
  );
};

export default PlatformPortalRoutes;
